import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { MoreVertical, Trash2 } from "lucide-react";
import {
  useGetEmailSuppressionsQuery,
  useAddEmailSuppressionMutation,
  useRemoveEmailSuppressionMutation,
} from "@/store/rest_api";
import { formatDateTime } from "@/util/date";
import { apiErrorMessage } from "@/util/apiError";

const SOURCE_LABELS: Record<string, string> = {
  UNSUBSCRIBE_LINK: "Unsubscribed",
  MANUAL: "Added by hand",
  COMPLAINT: "Spam complaint",
  BOUNCE: "Bounced",
};

export default function EmailSuppressionList() {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [newEmail, setNewEmail] = useState("");

  const { data, isLoading } = useGetEmailSuppressionsQuery({
    page,
    size: 50,
    search: search.trim() || undefined,
  });
  const [addSuppression, { isLoading: isAdding }] = useAddEmailSuppressionMutation();
  const [removeSuppression] = useRemoveEmailSuppressionMutation();

  const entries = data?.items;

  const handleAdd = async () => {
    const emailAddress = newEmail.trim();
    if (!emailAddress) {
      toast.error("Email address is required");
      return;
    }
    try {
      await addSuppression({ emailAddress }).unwrap();
      toast.success("Address added to the suppression list");
      setNewEmail("");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to add the address"));
    }
  };

  const handleRemove = async (emailAddress: string) => {
    try {
      await removeSuppression(emailAddress).unwrap();
      toast.success("Address removed, emails can be sent to it again");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to remove the address"));
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Addresses your organisation must not email. Anyone who uses the unsubscribe link in a workflow email lands
        here automatically, and workflow emails to these addresses are not sent.
      </p>

      <div className="flex items-center gap-3">
        <Input
          placeholder="name@example.com"
          value={newEmail}
          onChange={(event) => setNewEmail(event.target.value)}
          className="w-72"
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              void handleAdd();
            }
          }}
        />
        <Button onClick={() => void handleAdd()} disabled={isAdding}>
          Add
        </Button>
        <div className="flex-1" />
        <Input
          placeholder="Search addresses"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          className="w-64"
        />
      </div>

      {data && (
        <span className="text-sm text-muted-foreground">
          {data.totalElements} address{data.totalElements !== 1 ? "es" : ""}
        </span>
      )}

      {isLoading && <Skeleton className="h-64 w-full" />}

      {!isLoading && (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email Address</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Added</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries && entries.length > 0 ? (
                entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-mono">{entry.emailAddress}</TableCell>
                    <TableCell>
                      <Badge variant={entry.source === "UNSUBSCRIBE_LINK" ? "destructive" : "secondary"}>
                        {SOURCE_LABELS[entry.source] ?? entry.source}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {[entry.contact?.firstName, entry.contact?.lastName].filter(Boolean).join(" ") || "Not linked"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDateTime(entry.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Options">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => void handleRemove(entry.emailAddress)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Remove from list
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No suppressed email addresses.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {data.page + 1} of {data.totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" disabled={data.page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button
              variant="outline"
              disabled={data.page + 1 >= data.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
